import { useShadowRootSelection } from '@backstage/plugin-techdocs-react';
import {
  DOC_FEEDBACK_LIMITS,
  DocFeedbackCategory,
} from '@internal/backstage-plugin-doc-feedback-common';
import { Button } from '@material-ui/core';
import FeedbackOutlinedIcon from '@material-ui/icons/FeedbackOutlined';
import { useCallback, useEffect, useState } from 'react';
import { FeedbackDialog } from '../FeedbackDialog';
import {
  OPEN_FEEDBACK_EVENT,
  OpenFeedbackEventDetail,
  useDocPage,
} from './useDocPage';

type DialogState = {
  category?: DocFeedbackCategory;
  selectedText?: string;
  pageTitle?: string;
};

/**
 * "Give feedback" button rendered in the TechDocs subheader. Highlighted text
 * on the page is quoted in the feedback.
 */
export const GiveFeedbackAddon = () => {
  const { entityRef, pagePath, getPageTitle } = useDocPage();
  const selection = useShadowRootSelection(200);
  const [dialog, setDialog] = useState<DialogState>();

  const openDialog = useCallback(
    (category?: DocFeedbackCategory) => {
      const text = selection?.toString().trim();
      setDialog({
        category,
        selectedText: text
          ? text.slice(0, DOC_FEEDBACK_LIMITS.selectedText)
          : undefined,
        pageTitle: getPageTitle(),
      });
    },
    [selection, getPageTitle],
  );

  useEffect(() => {
    const listener = (event: Event) =>
      openDialog(
        (event as CustomEvent<OpenFeedbackEventDetail | undefined>).detail
          ?.category,
      );
    window.addEventListener(OPEN_FEEDBACK_EVENT, listener);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, listener);
  }, [openDialog]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<FeedbackOutlinedIcon />}
        // Keep the text highlighted on the page so it can be quoted
        onMouseDown={e => e.preventDefault()}
        onClick={() => openDialog()}
      >
        Give feedback
      </Button>
      {dialog && (
        <FeedbackDialog
          open
          onClose={() => setDialog(undefined)}
          entityRef={entityRef}
          pagePath={pagePath}
          pageTitle={dialog.pageTitle}
          selectedText={dialog.selectedText}
          initialCategory={dialog.category}
        />
      )}
    </>
  );
};
